import type { BuiltInModuleSlug } from '@insolitum/types';
import {
  Bot,
  Brain,
  Calendar,
  Droplets,
  Factory,
  Flame,
  HardHat,
  ScanEye,
  Sparkles,
  Target,
  Trophy,
  Truck,
  type LucideIcon,
} from 'lucide-react';

// =============================================================================
// Built-in module registry — the SINGLE source of truth for the shell's
// built-in modules. Previously this data was hardcoded and duplicated across
// federation.tsx (URLs + 52 wrapper components), App.tsx (routes),
// packages/types module.ts (MODULE_CONFIGS), ModulesPage.tsx (icons/colors/
// names) and AdminLayout/Mobile (ALL_MODULES). It now lives here once.
//
// Notes:
//  * `url` uses STATIC `import.meta.env.VITE_*` member access so Vite inlines it
//    at build time (dynamic `import.meta.env[key]` would NOT be replaced).
//  * `iframeRoutes` map a shell URL segment (`route`) to the path handed to the
//    embedded module (`path`) — they differ for aliases (e.g. statistics→/reports).
//  * `native` modules (nexus, ucs) wire their own explicit routes in App.tsx,
//    so they are excluded from iframe route generation but still appear in nav.
//
// KAŻDA podstrona modułu musi mieć tu wpis. Trasy powłoki powstają wyłącznie
// z `iframeRoutes` (generowany blok `/admin/modules/{slug}` w App.tsx), więc
// strona istniejąca w module, ale nieobecna w tym rejestrze, jest z powłoki
// nieosiągalna — także wtedy, gdy sam moduł wyśle
// `postMessage({ type: 'navigate' })` na jej segment. Tak z użycia wypadły m.in.
// Fabrica execution/quality/sourdough. Zgodności rejestru z katalogami
// `apps/module-*/src/app/*` pilnuje `src/modules/__tests__/registry.test.ts`.
// =============================================================================

export interface BuiltInIframeRoute {
  /** Shell URL segment under /admin/modules/<slug>/, or 'index' for the index route. */
  route: 'index' | string;
  /** Path handed to the embedded module iframe. */
  path: string;
}

export interface BuiltInModuleDef {
  slug: BuiltInModuleSlug;
  /** Full branded name shown in the module frame + marketplace card. */
  displayName: string;
  /** Short name shown in the sidebar / mobile nav. */
  navName: string;
  icon: LucideIcon;
  /** Tailwind text color for nav icons. */
  navColorClass: string;
  /** Marketplace card color set. */
  color: { bg: string; text: string; border: string };
  /** Resolved deploy URL (undefined for modules without an embedded frame). */
  url?: string;
  /** Iframe sub-routes (undefined for native modules). */
  iframeRoutes?: BuiltInIframeRoute[];
  /**
   * Segment tego modułu, pod którym leży JEGO zakładka Pracownicy — czyli
   * miejsce, w które kartoteka w `/admin/workers/:id` odsyła przyciskiem
   * „Otwórz w module". Każdy organ nazwał ten ekran inaczej (Temperatio
   * `workers`, LexShift `employees`, Aegis `profile`, Agenda `users`), więc
   * mapowanie musi być jawne.
   *
   * MUSI odpowiadać istniejącemu wpisowi w `iframeRoutes` — trasy powłoki
   * powstają wyłącznie z tamtej listy, więc link w segment bez wpisu jest
   * z powłoki nieosiągalny (tak z użycia wypadły ekrany Fabriki). Pilnuje
   * tego `src/modules/__tests__/registry.test.ts`.
   *
   * Ustawiony dla WSZYSTKICH SZEŚCIU organów, dla których migracja 442 liczy
   * metryki — bo kartoteka pokazuje wyniki każdego z nich, a wynik, którego nie
   * da się otworzyć u źródła, jest ślepym zaułkiem. Test rejestru tego pilnuje.
   *
   * Pominięte dla organów, które nikogo nie mierzą i nie mają własnego ekranu
   * pracowników (neurosense, provisio, nexus, ucs).
   */
  workerRoute?: string;
  /**
   * True for modules with explicit in-shell routes (nexus, ucs) — excluded
   * from generated iframe route blocks, but still listed in the nav.
   */
  native?: boolean;
}

const c = (bg: string, text: string, border: string) => ({ bg, text, border });

export const MODULE_REGISTRY: BuiltInModuleDef[] = [
  {
    slug: 'nexus',
    displayName: 'INSOLITUM NEXUS',
    navName: 'NEXUS AI',
    icon: Sparkles,
    navColorClass: 'text-emerald-400',
    color: c('bg-emerald-500/20', 'text-emerald-400', 'border-emerald-500/30'),
    native: true,
  },
  {
    slug: 'waterandice',
    displayName: 'INSOLITUM TEMPERATIO',
    navName: 'Temperatio',
    icon: Droplets,
    navColorClass: 'text-cyan-400',
    color: c('bg-cyan-500/20', 'text-cyan-400', 'border-cyan-500/30'),
    url: import.meta.env.VITE_WATERANDICE_URL || 'https://waterandice.insolitum.ai',
    workerRoute: 'workers',
    iframeRoutes: [
      { route: 'index', path: '/dashboard' },
      { route: 'products', path: '/products' },
      { route: 'workers', path: '/workers' },
      { route: 'measurements', path: '/measurements' },
      // Temperatio rozróżnia „Ciasta" (/doughs) i „Partie" (/batches) we własnej
      // nawigacji — wcześniejszy alias batches→/doughs pokazywał w powłoce
      // niewłaściwą stronę.
      { route: 'doughs', path: '/doughs' },
      { route: 'batches', path: '/batches' },
      { route: 'statistics', path: '/reports' },
      { route: 'reports', path: '/reports' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'neurosense',
    displayName: 'INSOLITUM NEUROSENSE IOT',
    navName: 'NeuroSense',
    icon: Brain,
    navColorClass: 'text-purple-400',
    color: c('bg-purple-500/20', 'text-purple-400', 'border-purple-500/30'),
    url: import.meta.env.VITE_NEUROSENSE_URL || 'https://neurosense.insolitum.ai',
    iframeRoutes: [
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'sensors', path: '/sensors' },
      // NeuroSense has no /devices page — its device list IS /sensors ("Sensory"
      // in the module's own nav). Kept as an alias so the segment resolves
      // instead of rendering a 404 inside the iframe.
      { route: 'devices', path: '/sensors' },
      { route: 'equipment', path: '/equipment' },
      { route: 'alerts', path: '/alerts' },
      { route: 'maintenance', path: '/maintenance' },
      { route: 'analytics', path: '/analytics' },
      { route: 'haccp', path: '/haccp' },
      { route: 'history', path: '/history' },
      { route: 'providers-list', path: '/providers-list' },
      { route: 'reports', path: '/reports' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'lexshift',
    displayName: 'INSOLITUM LEXSHIFT',
    navName: 'LexShift',
    icon: Calendar,
    navColorClass: 'text-green-400',
    color: c('bg-green-500/20', 'text-green-400', 'border-green-500/30'),
    url: import.meta.env.VITE_LEXSHIFT_URL || 'https://lexshift.insolitum.ai',
    workerRoute: 'employees',
    iframeRoutes: [
      { route: 'index', path: '/dashboard' },
      { route: 'employees', path: '/employees' },
      { route: 'schedules', path: '/schedules' },
      { route: 'shifts', path: '/shifts' },
      { route: 'sections', path: '/sections' },
      { route: 'positions', path: '/positions' },
      { route: 'holidays', path: '/holidays' },
      { route: 'attendance', path: '/attendance' },
      { route: 'leaves', path: '/leaves' },
      { route: 'dispositions', path: '/dispositions' },
      { route: 'organization', path: '/organization' },
      { route: 'pip-assistant', path: '/pip-assistant' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'gamification',
    displayName: 'INSOLITUM NEXUS AEGIS',
    navName: 'Aegis',
    icon: Trophy,
    navColorClass: 'text-amber-400',
    color: c('bg-amber-500/20', 'text-amber-400', 'border-amber-500/30'),
    url: import.meta.env.VITE_GAMIFICATION_URL || 'https://gamification.insolitum.ai',
    workerRoute: 'profile',
    iframeRoutes: [
      // Root modułu tylko przekierowuje na /leaderboard — celujemy od razu w cel,
      // żeby iframe nie mrugał ekranem przekierowania.
      { route: 'index', path: '/leaderboard' },
      { route: 'dashboard', path: '/leaderboard' },
      { route: 'leaderboard', path: '/leaderboard' },
      { route: 'achievements', path: '/achievements' },
      { route: 'skills', path: '/skills' },
      { route: 'activity', path: '/activity' },
      { route: 'profile', path: '/profile' },
      // Aegis names its admin area /config (there is no /settings page); the
      // `settings` segment stays as an alias for parity with every other module.
      { route: 'config', path: '/config' },
      { route: 'settings', path: '/config' },
      // Rozwój (umiejętności + plany rozwoju) — jedyny ekran ZAPISU kompetencji
      // w całym Universe, więc powłoka musi umieć w niego wejść wprost.
      { route: 'development', path: '/config/development' },
    ],
  },
  {
    slug: 'agenda',
    displayName: 'INSOLITUM AGENDA',
    navName: 'Agenda',
    icon: HardHat,
    navColorClass: 'text-orange-400',
    color: c('bg-orange-500/20', 'text-orange-400', 'border-orange-500/30'),
    url: import.meta.env.VITE_AGENDA_URL || 'https://agenda.insolitum.ai',
    workerRoute: 'users',
    iframeRoutes: [
      // Root modułu to tylko klientowe przekierowanie na /dashboard.
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'tasks', path: '/tasks' },
      { route: 'my-tasks', path: '/my-tasks' },
      { route: 'categories', path: '/categories' },
      { route: 'schedules', path: '/schedules' },
      { route: 'groups', path: '/groups' },
      { route: 'users', path: '/users' },
      { route: 'hub', path: '/hub' },
      { route: 'ai-assistant', path: '/ai-assistant' },
      { route: 'reports', path: '/reports' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'provisio',
    displayName: 'INSOLITUM PROVISIO',
    navName: 'Provisio',
    icon: Target,
    navColorClass: 'text-teal-400',
    color: c('bg-teal-500/20', 'text-teal-400', 'border-teal-500/30'),
    url: import.meta.env.VITE_PROVISIO_URL || 'https://provisio.insolitum.ai',
    iframeRoutes: [
      // Root modułu to tylko klientowe przekierowanie na /dashboard.
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'inventory', path: '/inventory' },
      // Partie surowca i ich atesty (W12) — sąsiad stanów magazynowych, bo
      // odpowiada na drugie pytanie o ten sam worek mąki: nie „ile go jest",
      // tylko „co pokazało badanie".
      { route: 'batches', path: '/batches' },
      { route: 'orders', path: '/orders' },
      { route: 'suppliers', path: '/suppliers' },
      { route: 'invoices', path: '/invoices' },
      { route: 'mrp', path: '/mrp' },
      { route: 'procurement', path: '/procurement' },
      { route: 'reports', path: '/reports' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'fabrica',
    displayName: 'INSOLITUM FABRICA',
    navName: 'Fabrica',
    icon: Factory,
    navColorClass: 'text-amber-400',
    color: c('bg-amber-500/20', 'text-amber-400', 'border-amber-500/30'),
    url: import.meta.env.VITE_FABRICA_URL || 'https://fabrica.insolitum.ai',
    workerRoute: 'workers',
    iframeRoutes: [
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'hala', path: '/hala' },
      { route: 'workers', path: '/workers' },
      { route: 'production', path: '/production' },
      { route: 'execution', path: '/execution' },
      { route: 'quality', path: '/quality' },
      { route: 'defects', path: '/defects' },
      { route: 'downtime', path: '/downtime' },
      { route: 'capabilities', path: '/capabilities' },
      { route: 'sourdough', path: '/sourdough' },
      { route: 'consumption', path: '/consumption' },
      { route: 'waste', path: '/waste' },
      { route: 'recipes', path: '/recipes' },
      { route: 'surowce', path: '/surowce' },
      { route: 'propozycje', path: '/propozycje' },
      { route: 'czasy', path: '/czasy' },
      { route: 'schedules', path: '/schedules' },
      { route: 'lancuch', path: '/lancuch' },
      { route: 'workstations', path: '/workstations' },
      { route: 'instruments', path: '/instruments' },
      { route: 'shifts', path: '/shifts' },
      { route: 'wizard', path: '/wizard' },
      { route: 'migration', path: '/migration' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'conspectus',
    displayName: 'INSOLITUM NEXUS CONSPECTUS',
    navName: 'Conspectus',
    // ScanEye, nie Eye: samo oko to ikona INWIGILACJI — dokładnie tego rejestru
    // unikaliśmy przy wyborze nazwy, więc stawianie go na karcie w cenniku
    // odwracałoby tamtą decyzję jednym obrazkiem. Nawiasy skanu przesuwają
    // odczyt z „ktoś patrzy" na „urządzenie analizuje kadr", a to jest ten
    // moduł. Odpadły też: `Cctv` (sylwetka kamery monitoringu — ten sam
    // problem co oko), `Camera` (mówi „zdjęcia", nie „ocena") i `Focus`,
    // który przy 16 px w menu jest nie do odróżnienia od `Target` Provisia.
    icon: ScanEye,
    navColorClass: 'text-sky-400',
    color: c('bg-sky-500/20', 'text-sky-400', 'border-sky-500/30'),
    url: import.meta.env.VITE_CONSPECTUS_URL || 'https://conspectus.insolitum.ai',
    workerRoute: 'workers',
    iframeRoutes: [
      // Root modułu tylko przekierowuje na /dashboard — celujemy od razu w cel.
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'workers', path: '/workers' },
      { route: 'cameras', path: '/cameras' },
      { route: 'gateways', path: '/gateways' },
      { route: 'observations', path: '/observations' },
      { route: 'faces', path: '/faces' },
      { route: 'findings', path: '/findings' },
      { route: 'incidents', path: '/incidents' },
      // 389 — rejestr usterek obserwowalności; sprawy nierozstrzygnięte
      // przestały być zadaniami Agendy i lądują tutaj.
      { route: 'observability', path: '/observability' },
      { route: 'workflow', path: '/workflow' },
      { route: 'journal', path: '/journal' },
      { route: 'requirements', path: '/requirements' },
      { route: 'settings', path: '/settings' },
    ],
  },
  {
    slug: 'artifex',
    displayName: 'INSOLITUM ARTIFEX',
    navName: 'Artifex',
    // Bot, nie Cpu ani Wrench: organ nie jest „urządzeniem" (te należą do
    // NeuroSense) ani „narzędziem", tylko pracownikiem, który uczy się zawodu.
    // Sylwetka humanoida odróżnia go od `Brain` NeuroSense'a przy 16 px mimo
    // sąsiedztwa palet — a to jest ta odległość, przy której odpadł `Focus`
    // Conspectusa.
    icon: Bot,
    navColorClass: 'text-violet-400',
    color: c('bg-violet-500/20', 'text-violet-400', 'border-violet-500/30'),
    url: import.meta.env.VITE_ARTIFEX_URL || 'https://artifex.insolitum.ai',
    // Bez `workerRoute`: organ NIE MA własnego ekranu pracowników i nikogo nie
    // mierzy. Pracownik pojawia się w nim wyłącznie jako autor zgody na
    // wykorzystanie nagrania, a ekran zgód celowo nie jest kartoteką — patrz
    // reguła prywatności w `apps/module-artifex/src/app/consents/page.tsx`.
    iframeRoutes: [
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'robots', path: '/robots' },
      { route: 'curation', path: '/curation' },
      { route: 'academy', path: '/academy' },
      { route: 'consents', path: '/consents' },
    ],
  },
  {
    slug: 'furnus',
    displayName: 'INSOLITUM FURNUS',
    navName: 'Furnus',
    // Flame — ten sam znak co w katalogu `modules` (migracja 740) i w panelu
    // organu. Czerwień jest jedynym wolnym akcentem rejestru i niesie „ogień
    // pieca"; stanów (błąd, ostrzeżenie) organ nie koduje kolorem znaku.
    icon: Flame,
    navColorClass: 'text-red-400',
    color: c('bg-red-500/20', 'text-red-400', 'border-red-500/30'),
    url: import.meta.env.VITE_FURNUS_URL || 'https://furnus.insolitum.ai',
    // Bez `workerRoute`: piecowy pracuje na tablecie hali (`:feature:furnus`),
    // a panel nie ma (jeszcze) ekranu pracowników organu.
    iframeRoutes: [
      { route: 'index', path: '/dashboard' },
      { route: 'dashboard', path: '/dashboard' },
      { route: 'piece', path: '/piece' },
      { route: 'produkty', path: '/produkty' },
      { route: 'raporty', path: '/raporty' },
      { route: 'audyt', path: '/audyt' },
      { route: 'ustawienia', path: '/ustawienia' },
    ],
  },
  {
    slug: 'ucs',
    displayName: 'UCS SHIPPING',
    navName: 'UCS',
    icon: Truck,
    navColorClass: 'text-amber-400',
    color: c('bg-amber-500/20', 'text-amber-400', 'border-amber-500/30'),
    url: import.meta.env.VITE_UCS_URL || 'https://ucs.insolitum.ai',
    // native: the explicit /admin/modules/ucs route (UcsModulePage) fetches a
    // one-time SSO ticket before mounting the iframe — no generated routes.
    native: true,
  },
];

const REGISTRY_BY_SLUG = new Map<string, BuiltInModuleDef>(MODULE_REGISTRY.map((m) => [m.slug, m]));

/** Look up a built-in module definition by slug. */
export function getModuleDef(slug: string): BuiltInModuleDef | undefined {
  return REGISTRY_BY_SLUG.get(slug);
}

/** Branded display name for a module slug, with a humanized fallback. */
export function moduleDisplayName(slug: string): string {
  return getModuleDef(slug)?.displayName ?? slug;
}

/** Nav-list shape consumed by the sidebar (AdminLayout) and mobile menus. */
export interface ModuleNavItem {
  id: BuiltInModuleSlug;
  icon: LucideIcon;
  color: string;
  name: string;
}

export const MODULE_NAV_ITEMS: ModuleNavItem[] = MODULE_REGISTRY.map((m) => ({
  id: m.slug,
  icon: m.icon,
  color: m.navColorClass,
  name: m.navName,
}));

// ─── Organy, do których przypisuje się pracownika ───────────────────────────
//
// Kartoteka (`/admin/workers`) pokazywała dotąd cztery moduły z RĘCZNIE
// przepisanej listy w `WorkersPage.tsx` — bez importu stąd, więc lista
// rozjeżdżała się z rejestrem po cichu. Aegis wypadł z niej na tyle długo, że
// karta pracownika nie miała jak powiedzieć, że ten organ go dotyczy, i naprawa
// jest do dziś odnotowana komentarzem w tamtym pliku. Od teraz źródło jest
// jedno.
//
// Wykluczamy `nexus` i `ucs`: pierwszy nie ma w warstwie webowej ani jednego
// odwołania do `worker_id` (jego dane pracownicze należą do `services/nexus-core`
// i aplikacji mobilnej), drugi to zewnętrzny spedytor. Reszta zostaje — także
// organy, które dziś jeszcze nikogo nie mierzą (NeuroSense, Provisio), bo
// „przypisany, ale bez danych" to informacja dla kadrowej, a nie błąd do
// ukrycia.

const NIE_DLA_PRACOWNIKOW: ReadonlySet<string> = new Set(['nexus', 'ucs']);

/** Moduły, do których w ogóle da się przypisać pracownika — w kolejności rejestru. */
export const WORKER_MODULES: BuiltInModuleDef[] = MODULE_REGISTRY.filter(
  (m) => !NIE_DLA_PRACOWNIKOW.has(m.slug)
);

/** Same slugi — argument `allSlugs` helperów z `@insolitum/utils`. */
export const WORKER_MODULE_SLUGS: string[] = WORKER_MODULES.map((m) => m.slug);

/**
 * Trasa powłoki do ekranu pracowników danego organu, albo `null`, gdy organ
 * takiego ekranu nie ma. Buduje ścieżkę z `workerRoute`, więc nie da się
 * wskazać segmentu, którego rejestr nie zna.
 */
export function moduleWorkerRoute(slug: string): string | null {
  const def = getModuleDef(slug);
  if (!def?.workerRoute) return null;
  return `/admin/modules/${def.slug}/${def.workerRoute}`;
}

/**
 * Trasa powłoki do ekranu modułu montowanego w iframe, wskazanego ŚCIEŻKĄ aplikacji modułu (np. `/products`),
 * albo `null`, gdy rejestr takiego ekranu nie zna. Ścieżkę liczy się z `iframeRoutes`, nie wpisuje drugi raz —
 * tak samo jak `moduleWorkerRoute`. Pierwszy użytkownik: „Ustaw w Temperatio” w kartotece centralnej
 * (Temperatio A+, 26.10.2026), który prowadzi na ekran „Produkty” Temperatio.
 */
export function moduleScreenRoute(slug: string, sciezkaModulu: string): string | null {
  const def = getModuleDef(slug);
  const trasa = def?.iframeRoutes?.find((r) => r.path === sciezkaModulu);
  if (!def || !trasa) return null;
  return trasa.route === 'index'
    ? `/admin/modules/${def.slug}`
    : `/admin/modules/${def.slug}/${trasa.route}`;
}
